/* eslint-disable react/jsx-props-no-spreading */
import { Form, Row } from 'antd'

import { Formik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef } from 'react'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import { formElementPropsGenerator } from '../../lib/forms'
import { DatePicker, Input, SubmitButton, TextArea } from '../Forms'
import Switch from '../Forms/Switch'
import PatientSelect from '../Forms/PatientSelect'
import { useIsAdmin } from '../../redux/slices/session'
import MedicalProviderSelect from '../Forms/MedicalProviderSelect'
import api from '../../api'

const PRE_AUTHORIZATION_LABELS = {
  cptCode: 'CPT Code',
  icd10Code: 'ICD-10 Code',
  patientId: 'Patient',
  billingDate: 'Billing Date',
  authorized: 'Authorized',
  medicalProviderId: 'Medical Provider',
  insuranceAuthorizationNumber: 'Insurance Authorization Number',
  notes: 'Notes',
}

const propsFor = formElementPropsGenerator({
  formName: 'pre-authorization-form',
  labels: PRE_AUTHORIZATION_LABELS,
})

const labelCol = {
  sm: { span: 4, offset: 2 },
}

const wrapperCol = {
  sm: { span: 16 },
}

const actionsWrapperCol = {
  sm: { span: 4, offset: labelCol.sm.span + labelCol.sm.offset },
}

function buildValidationSchema(isNew = false, isAdmin = false) {
  const shape = {
    authorized: Yup.bool().required(),
    medicalProviderId: Yup.string().nullable(),
    insuranceAuthorizationNumber: Yup.string().nullable(),
    notes: Yup.string().nullable(),
  }

  if (isNew) {
    shape.patientId = Yup.string().required()
  }

  if (isAdmin) {
    shape.cptCode = Yup.string().required()
    shape.icd10Code = Yup.string().required()
    shape.billingDate = Yup.date().required()
  }

  return Yup.object().shape(shape)
}

function FetchSuggestedCodesOnPatientChange({
  patientId,
  cycleId,
  isAdmin,
  setFieldValue,
}) {
  const latestRequestedIdRef = useRef(null)
  useEffect(() => {
    if (!cycleId || !isAdmin) return
    if (!patientId) {
      latestRequestedIdRef.current = null
      setFieldValue('cptCode', '')
      setFieldValue('icd10Code', '')
      setFieldValue('medicalProviderId', null)
      return
    }
    const requestedId = patientId
    latestRequestedIdRef.current = requestedId
    api.preAuthorizations
      .getSuggestedCodes(cycleId, requestedId)
      .then(result => {
        if (latestRequestedIdRef.current !== requestedId) return
        setFieldValue('cptCode', result?.cptCode ?? '')
        setFieldValue('icd10Code', result?.icd10Code ?? '')
        setFieldValue(
          'medicalProviderId',
          result?.medicalProviderId ?? null,
        )
      })
      .catch(() => {
        if (latestRequestedIdRef.current !== requestedId) return
        setFieldValue('cptCode', '')
        setFieldValue('icd10Code', '')
        setFieldValue('medicalProviderId', null)
      })
  }, [patientId, cycleId, isAdmin, setFieldValue])
  return null
}

FetchSuggestedCodesOnPatientChange.propTypes = {
  patientId: PropTypes.string,
  cycleId: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
}

export default function PreAuthorizationForm({
  onSubmit,
  practiceId = undefined,
  cycleId = undefined,
  endsAt = undefined,
  preAuthorization = {
    authorized: false,
    patient: {
      practiceId: undefined,
    },
  },
}) {
  const isAdmin = useIsAdmin()
  const isNew = !preAuthorization.id
  const validationSchema = useMemo(
    () => buildValidationSchema(isNew, isAdmin),
    [isAdmin, isNew],
  )
  const initialPracticeId = practiceId || preAuthorization.patient.practiceId
  const initialValues = {
    ...preAuthorization,
    billingDate: preAuthorization.billingDate || (endsAt ? dayjs(endsAt) : undefined)
  }
  const selectLabelCol = { sm: 3, offset: 3 }
  const selectWrapperCol = { sm: 12, offset: 0 }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={isNew}
        onSubmit={onSubmit}
      >
        {({
          handleSubmit,
          isSubmitting,
          submitCount,
          setFieldValue,
          values,
        }) => {
          const patientId =
            values.patientId && typeof values.patientId === 'object'
              ? values.patientId?.value
              : values.patientId

          return (
            <>
              <FetchSuggestedCodesOnPatientChange
                patientId={patientId}
                cycleId={cycleId}
                isAdmin={isAdmin}
                setFieldValue={setFieldValue}
              />
            <Form
              labelCol={labelCol}
              wrapperCol={wrapperCol}
              onFinish={handleSubmit}
            >
              {isNew && (
                <PatientSelect
                  {...propsFor('patientId')}
                  practiceId={practiceId}
                  style={{ minWidth: 200 }}
                  size="medium"
                  wrapperItem
                />
              )}
              {isAdmin && (
                <>
                  <Input {...propsFor('cptCode')} />
                  <Input {...propsFor('icd10Code')} />
                  <DatePicker {...propsFor('billingDate')} allowClear={false} maxDate={null} />
                  <Input {...propsFor('insuranceAuthorizationNumber')} />
                  <TextArea {...propsFor('notes')} />
                </>
              )}
              <Switch
                {...propsFor('authorized')}
                checkedChildren="Authorized"
                unCheckedChildren="Not&nbsp;authorized"
              />

                <MedicalProviderSelect
                  {...propsFor('medicalProviderId')}
                  practiceId={initialPracticeId}
                  displayNPI
                  labelCol={selectLabelCol}
                  wrapperCol={selectWrapperCol}
                  wrapperItem
                />

              <Row>
                <SubmitButton
                  isSubmitting={isSubmitting}
                  title={
                    isNew
                      ? 'Create Remote Authorization'
                      : 'Save Remote Authorization'
                  }
                  isValid
                  submitCount={submitCount}
                  wrapperCol={actionsWrapperCol}
                />
              </Row>
            </Form>
            </>
          )
        }}
      </Formik>
    </div>
  )
}

PreAuthorizationForm.propTypes = {
  practiceId: PropTypes.string,
  cycleId: PropTypes.string,
  preAuthorization: PropTypes.shape({
    comment: PropTypes.string,
    id: PropTypes.string,
    patient: PropTypes.shape({
      practiceId: PropTypes.string,
    }),
  }),
  onSubmit: PropTypes.func.isRequired,
  patient: PropTypes.shape({
    practiceId: PropTypes.string,
  }),
  endsAt: PropTypes.string,
}
